/**
 * "Aggiungi workspace" — the second self-serve purchase door (ADR-0079).
 *
 * It became a two-step dialog when the invoicing profile turned MANDATORY (owner,
 * 2026-07-28 — *"a chi facciamo fattura?"*): the address + plan is one decision, who
 * to invoice is another, and eight fiscal fields do not fit the inline row the button
 * used to open. Step 2 is the SAME `BillingFieldset` the signup wizard shows, so the
 * two doors ask for exactly the same things.
 *
 * The profile is the WORKSPACE's own, not the organization's — every deployment is
 * invoiced on its own (ADR-0076), so nothing is prefilled from the company here.
 *
 * Nothing is created on submit: what comes back is a Stripe Checkout URL, and the
 * payment webhook provisions the workspace.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BillingFieldset } from "@/components/billing/BillingFieldset";
import {
  type BillingForm,
  EMPTY_BILLING,
  billingComplete,
  toBillingPayload,
} from "@/lib/billingProfile";
import { identityApi, type OnboardingPlan } from "@/lib/api-onboarding";

export function BuyWorkspaceDialog({
  organizationId,
  organizationName,
  plans,
  open,
  onOpenChange,
}: {
  organizationId: string;
  organizationName: string;
  /** `null` while the plan catalog is still loading. */
  plans: OnboardingPlan[] | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = React.useState<0 | 1>(0);
  const [slug, setSlug] = React.useState("");
  const [name, setName] = React.useState("");
  const [planKey, setPlanKey] = React.useState("");
  const [billing, setBilling] = React.useState<BillingForm>(EMPTY_BILLING);
  const [billingTouched, setBillingTouched] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Every open starts clean: a half-filled purchase left over from a cancelled
  // attempt is the kind of state that gets paid for by accident.
  React.useEffect(() => {
    if (!open) return;
    setStep(0);
    setSlug("");
    setName("");
    setPlanKey("");
    setBilling(EMPTY_BILLING);
    setBillingTouched(false);
    setError(null);
  }, [open]);

  const setB = <K extends keyof BillingForm>(key: K, value: BillingForm[K]) =>
    setBilling((b) => ({ ...b, [key]: value }));

  const stepOneValid = slug.trim().length > 0 && planKey.length > 0;
  const billingValid = billingComplete(billing);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (step === 0) {
      if (stepOneValid) setStep(1);
      return;
    }
    if (!billingValid) {
      setBillingTouched(true);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const res = await identityApi.createOrgWorkspace(organizationId, {
        slug: slug.trim(),
        name: name.trim() || undefined,
        plan_key: planKey,
        billing: toBillingPayload(billing),
      });
      // The workspace does not exist yet: we leave the SPA for Stripe's page, and the
      // payment webhook is what provisions it.
      window.location.href = res.checkout_url;
    } catch (err) {
      // The backend refuses by NAME, so say which one it was: a taken address is the
      // visitor's to fix, a missing arc is the owner's.
      const msg = err instanceof Error ? err.message : "";
      setError(
        msg.includes("not_org_admin")
          ? t("identityAccount.notOrgAdmin", "Non sei amministratore di questa organizzazione.")
          : msg.includes("slug_exists")
            ? t("identityAccount.slugTaken", "Questo indirizzo è già in uso.")
            : msg.includes("billing:incomplete")
              ? t(
                  "identityAccount.billingIncomplete",
                  "Mancano dei dati di fatturazione obbligatori.",
                )
              : msg.includes("self_serve_unavailable")
                ? t(
                    "identityAccount.purchaseUnavailable",
                    "I pagamenti non sono configurati su questa installazione.",
                  )
                : t("identityAccount.wsCreateFailed", "Creazione del workspace non riuscita."),
      );
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("identityAccount.addWorkspace", "Aggiungi workspace")} · {organizationName}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => void submit(e)} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="space-y-4">
            <p className="text-xs text-muted-foreground">
              {step === 0
                ? t("identityAccount.wsStep1", "1 di 2 · Workspace e piano")
                : t("identityAccount.wsStep2", "2 di 2 · Dati di fatturazione")}
            </p>

            {step === 0 ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="ws-slug">
                    {t("identityAccount.wsSlug", "Indirizzo del workspace")}
                  </Label>
                  <Input
                    id="ws-slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="acme"
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ws-name">{t("identityAccount.wsName", "Nome (opzionale)")}</Label>
                  <Input id="ws-name" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ws-plan">{t("identityAccount.wsPlan", "Piano")}</Label>
                  <select
                    id="ws-plan"
                    className="w-full"
                    value={planKey}
                    onChange={(e) => setPlanKey(e.target.value)}
                  >
                    <option value="">{t("identityAccount.wsPlanPick", "Scegli…")}</option>
                    {(plans ?? []).map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <BillingFieldset
                t={t}
                billing={billing}
                setB={setB}
                showErrors={billingTouched}
              />
            )}

            {error && (
              <Alert tone="danger" title={t("identityAccount.errorTitle", "Errore")}>
                {error}
              </Alert>
            )}
          </DialogBody>
          <DialogFooter>
            {step === 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(0)}
                disabled={busy}
              >
                {t("onboarding.back", "Indietro")}
              </Button>
            )}
            <Button type="submit" disabled={busy || (step === 0 && !stepOneValid)}>
              {step === 0
                ? t("onboarding.next", "Avanti")
                : t("identityAccount.wsBuy", "Vai al pagamento")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
